import { Component } from '@theme/component';

/**
 * Tolerance, in pixels, when deciding whether the track sits at either end.
 * @type {number}
 */
const EDGE_EPSILON = 2;

/**
 * Mini carousel listing the fragrances inside a coffret.
 *
 * The track is a native scroll-snap scroller, so touch, trackpad and keyboard
 * scrolling work on their own. This component adds previous/next buttons that
 * move by one card and disables each one at its end of the track.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scroll container.
 * @property {HTMLButtonElement} [previous] - Scrolls back one card.
 * @property {HTMLButtonElement} [next] - Scrolls forward one card.
 *
 * @extends Component<Refs>
 */
class CspCoffretContentsComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', this.#updateArrows, { passive: true, signal });
    window.addEventListener('resize', this.#updateArrows, { passive: true, signal });
    this.#updateArrows();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** Scrolls back by one card. */
  previous() {
    this.#scrollByCard(-1);
  }

  /** Scrolls forward by one card. */
  next() {
    this.#scrollByCard(1);
  }

  /**
   * @param {1 | -1} direction
   */
  #scrollByCard(direction) {
    const { track } = this.refs;
    const slide = track.firstElementChild;
    if (!(slide instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const sign = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * sign * (slide.offsetWidth + gap) });
  }

  #updateArrows = () => {
    const { track, previous, next } = this.refs;
    const scrolled = Math.abs(track.scrollLeft);
    const maxScroll = track.scrollWidth - track.clientWidth;

    if (previous) previous.disabled = scrolled <= EDGE_EPSILON;
    if (next) next.disabled = scrolled >= maxScroll - EDGE_EPSILON;
  };
}

if (!customElements.get('csp-coffret-contents')) {
  customElements.define('csp-coffret-contents', CspCoffretContentsComponent);
}
